import { GrcTabPanel } from '@grc/ui-package';
import { useI18nState } from '../../../stores/settings/language-context';
import { shipmentTabsConfig } from '../shipments.config';
import { ShipmentTabKey } from '../../../models/shipments/shipment-tab.interface';
import { IDateRangeFilterValue } from '../../../common/date-range-filter/date-range-filter.interface';
import WorkItemDetailsGrid from '../work-item-details/work-item-details-grid';
import ShipmentHeatMap from '../heat-map/shipment-heat-map';
import './shipment-tabs.scss';

interface IShipmentTabsProps {
  activeTab: ShipmentTabKey;
  onTabChange: (tab: ShipmentTabKey) => void;
  searchText: string;
  dateFilter: IDateRangeFilterValue;
  // Structural rather than IShipment/IShipmentGridRow-specific - the heat map hands back an
  // IShipment, every other tab an IShipmentGridRow, and shipments-page.tsx's handler only
  // ever reads shipmentNumber off whichever one it gets.
  onShipmentIdClick: (shipment: { shipmentNumber: string }) => void;
}

interface IShipmentTabItem {
  id: ShipmentTabKey;
  title: string;
}

export default function ShipmentTabs({
  activeTab,
  onTabChange,
  searchText,
  dateFilter,
  onShipmentIdClick
}: IShipmentTabsProps) {
  const { localeData } = useI18nState();

  const dataSource: IShipmentTabItem[] = shipmentTabsConfig.map(tab => ({
    id: tab.key,
    title: localeData[tab.labelKey] as string
  }));

  const selectedIndex = Math.max(
    dataSource.findIndex(item => item.id === activeTab),
    0
  );

  return (
    <GrcTabPanel
      className="grc-tabs shipment-tabs"
      dataSource={dataSource}
      selectedIndex={selectedIndex}
      swipeEnabled={false}
      onSelectedIndexChange={(index: number) => {
        const nextKey = dataSource[index]?.id;
        if (nextKey && nextKey !== activeTab) {
          onTabChange(nextKey);
        }
      }}
      itemRender={(item: IShipmentTabItem) => {
        if (item.id === 'heatMap') {
          return (
            <ShipmentHeatMap
              searchText={searchText}
              dateFilter={dateFilter}
              onShipmentIdClick={onShipmentIdClick}
            />
          );
        }

        const tab = shipmentTabsConfig.find(t => t.key === item.id)?.tab;
        if (!tab) return null;

        return (
          <WorkItemDetailsGrid
            tab={tab}
            dateFilter={dateFilter}
            searchText={searchText}
            onShipmentIdClick={onShipmentIdClick}
          />
        );
      }}
    />
  );
}
